import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import equipmentService from '../api/equipmentService';
import projectService from '../api/projectService';
import Modal from '../components/Modal';
import EquipmentForm from '../components/EquipmentForm';
import Button from '../components/Button';
import RowActions from '../components/RowActions';
// Estados de carga/error compartidos (ver hallazgo de auditoría #08)
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';

/**
 * @description Página para la gestión de Equipos.
 * Permite ver, crear, editar y eliminar equipos. Incluye búsqueda por texto.
 */
const Equipment = () => {
  // Lee el parámetro ?projectId de la URL para pre-filtrar por proyecto
  const [searchParams, setSearchParams] = useSearchParams();
  const initialProjectId = searchParams.get('projectId') || '';

  const [equipments, setEquipments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentEquipment, setCurrentEquipment] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [searchText, setSearchText] = useState('');
  // Filtro por proyecto: se inicializa desde la URL y se puede limpiar manualmente
  const [projectIdFilter, setProjectIdFilter] = useState(initialProjectId);

  useEffect(() => {
    fetchData();
  }, []);

  const showActionError = (message) => {
    setActionError(message);
    setTimeout(() => setActionError(''), 6000);
  };

  const fetchData = async () => {
    setLoadError('');
    try {
      const [equipRes, projRes] = await Promise.all([
        equipmentService.getEquipments(),
        projectService.getProjectsLookup(),
      ]);
      setEquipments(equipRes.data);
      setProjects(projRes.data.data);
    } catch (error) {
      console.error('Error al cargar equipos:', error);
      setLoadError('No se pudo cargar la lista de equipos. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (currentEquipment) {
        await equipmentService.updateEquipment(currentEquipment.equipment_id, data);
      } else {
        await equipmentService.createEquipment(data);
      }
      fetchData();
      closeModal();
    } catch (error) {
      console.error('Error al guardar equipo:', error);
      showActionError(error.response?.data?.message || 'No se pudo guardar el equipo.');
    }
  };

  const handleDelete = (id) => {
    setConfirmDeleteId(id);
  };

  const executeDelete = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await equipmentService.deleteEquipment(id);
      fetchData();
    } catch (error) {
      console.error('Error al eliminar equipo:', error);
      showActionError(error.response?.data?.message || 'No se pudo eliminar el equipo.');
    }
  };

  const openModal = (equipment = null) => {
    setCurrentEquipment(equipment);
    setShowModal(true);
  };

  const closeModal = () => {
    setCurrentEquipment(null);
    setShowModal(false);
  };

  /**
   * @description Limpia el filtro de proyecto activo y elimina el parámetro de la URL.
   */
  const clearProjectFilter = () => {
    setProjectIdFilter('');
    setSearchParams({});
  };

  // Nombre del proyecto filtrado, para mostrarlo en el aviso activo
  const activeProjectName = projectIdFilter
    ? projects.find((p) => String(p.project_id) === String(projectIdFilter))?.name || `ID ${projectIdFilter}`
    : null;

  const filteredEquipments = equipments.filter((e) => {
    // Filtro por proyecto cuando viene desde la URL (?projectId=X)
    if (projectIdFilter && String(e.project_id) !== String(projectIdFilter)) return false;
    const query = searchText.toLowerCase();
    return (
      query === '' ||
      e.name?.toLowerCase().includes(query) ||
      e.brand?.toLowerCase().includes(query) ||
      e.project?.name?.toLowerCase().includes(query) ||
      String(e.installation_year).includes(query)
    );
  });

  if (loading) return <LoadingState message="Cargando equipos..." />;
  if (loadError) return <ErrorState message={loadError} />;

  return (
    <div>
      <h2>Equipos</h2>

      {/* Aviso de filtro activo por proyecto — visible cuando se llega desde /projects */}
      {projectIdFilter && (
        <div style={styles.filterNoticeBanner}>
          <span>
            Mostrando equipos del proyecto: <strong>{activeProjectName}</strong>
          </span>
          <button
            onClick={clearProjectFilter}
            style={styles.filterNoticeClear}
            aria-label="Quitar filtro de proyecto"
          >
            &times; Quitar filtro
          </button>
        </div>
      )}

      {actionError && (
        <div style={styles.actionErrorBanner}>
          <span>{actionError}</span>
          <button
            onClick={() => setActionError('')}
            style={styles.actionErrorClose}
            aria-label="Cerrar mensaje de error"
          >
            &times;
          </button>
        </div>
      )}

      <div style={styles.toolbar}>
        <input
          type="text"
          placeholder="Buscar por nombre, marca o proyecto..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={styles.searchInput}
        />
        {/* Acción primaria de la página: relleno sólido en teal de marca */}
        <Button variant="primary" onClick={() => openModal()} style={{ marginLeft: 'auto' }}>
          Crear Nuevo Equipo
        </Button>
      </div>

      <p style={styles.resultCount}>
        {filteredEquipments.length}{' '}
        {filteredEquipments.length === 1 ? 'equipo encontrado' : 'equipos encontrados'}
      </p>

      {filteredEquipments.length === 0 ? (
        <p style={styles.emptyMessage}>
          {searchText || projectIdFilter
            ? 'No se encontraron equipos con los filtros aplicados.'
            : 'No hay equipos registrados.'}
        </p>
      ) : (
        <div className="table-container">
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>Nombre</th>
              <th style={styles.th}>Marca</th>
              <th style={styles.th} className="col-numeric">Año instalación</th>
              <th style={styles.th}>Proyecto</th>
              <th style={styles.th}>Comentarios</th>
              <th style={styles.th} className="col-actions-sticky">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredEquipments.map((e) => (
              <tr key={e.equipment_id} style={styles.row}>
                <td style={styles.td}>{e.name}</td>
                <td style={styles.td}>{e.brand}</td>
                <td style={styles.td} className="col-numeric">{e.installation_year}</td>
                <td style={styles.td}>{e.project?.name || '—'}</td>
                <td style={styles.td}>{e.comments || '—'}</td>
                <td style={styles.td} className="col-actions-sticky">
                  {/* Editar: tinte teal, acción esperada. Eliminar: solo-ícono,
                      neutro en reposo y rojo recién al hover — ver RowActions.js */}
                  <RowActions
                    onEdit={() => openModal(e)}
                    onDelete={() => handleDelete(e.equipment_id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      <Modal
        show={showModal}
        onClose={closeModal}
        title={currentEquipment ? 'Editar Equipo' : 'Crear Equipo'}
      >
        <EquipmentForm
          equipment={currentEquipment}
          projects={projects}
          onSave={handleSave}
          onCancel={closeModal}
        />
      </Modal>

      <Modal
        show={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        title="Eliminar equipo"
      >
        <p style={{ margin: 0, color: '#444' }}>
          ¿Está seguro de que desea eliminar este equipo? Dejará de aparecer en el sistema, pero sus reportes de mantenimiento se conservan.
        </p>
        <div className="modal-footer">
          <button onClick={() => setConfirmDeleteId(null)} className="modal-btn-secondary">
            Cancelar
          </button>
          <button onClick={executeDelete} className="modal-btn-danger">
            Eliminar
          </button>
        </div>
      </Modal>
    </div>
  );
};

const styles = {
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  searchInput: {
    padding: '8px',
    minWidth: '260px',
    flex: '1',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  resultCount: {
    fontSize: '0.85rem',
    color: '#666',
    marginBottom: '8px',
  },
  // Densidad, zebra, hover y columna de acciones fija ahora viven en
  // responsive.css (.table-container table ...) — ver hallazgo de auditoría #04.
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {},
  th: {},
  row: {},
  td: {},
  emptyMessage: {
    color: '#777',
    fontStyle: 'italic',
  },
  // Banner que indica que la lista está filtrada por un proyecto específico
  filterNoticeBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#e8f4fd',
    color: '#1a6496',
    border: '1px solid #bce0fd',
    borderRadius: '4px',
    padding: '8px 14px',
    marginBottom: '14px',
    fontSize: '0.9rem',
  },
  filterNoticeClear: {
    background: 'none',
    border: 'none',
    color: '#1a6496',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 'bold',
    padding: '0 0 0 12px',
    textDecoration: 'underline',
  },
  actionErrorBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fdecea',
    color: '#c0392b',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    padding: '10px 14px',
    marginBottom: '14px',
    fontSize: '0.9rem',
  },
  actionErrorClose: {
    background: 'none',
    border: 'none',
    color: '#c0392b',
    cursor: 'pointer',
    fontSize: '1.2rem',
    lineHeight: '1',
    padding: '0 0 0 12px',
    fontWeight: 'bold',
  },
};

export default Equipment;
